import { forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide } from "d3-force";

export type NodeInput = {
  id: string;
  label?: string;
  kind?: "source" | "transform" | "destination";
  state?: "idle" | "active" | "failure";
};
export type EdgeInput = { source: string; target: string };

export type LaidOutNode = NodeInput & { x: number; y: number };
export type LaidOutEdge = EdgeInput & {
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
};

export function layoutGraph({
  nodes,
  edges,
  width,
  height,
  ticks = 300,
}: {
  nodes: NodeInput[];
  edges: EdgeInput[];
  width: number;
  height: number;
  ticks?: number;
}): { nodes: LaidOutNode[]; edges: LaidOutEdge[] } {
  const simNodes: any[] = nodes.map((n) => ({ ...n }));
  const simEdges: any[] = edges.map((e) => ({ ...e }));

  const sim = forceSimulation(simNodes)
    .force(
      "link",
      forceLink(simEdges)
        .id((d: any) => d.id)
        .distance(90),
    )
    .force("charge", forceManyBody().strength(-180))
    .force("center", forceCenter(width / 2, height / 2))
    .force("collide", forceCollide(22))
    .stop();

  for (let i = 0; i < ticks; i++) sim.tick();

  const clamp = (v: number, lo: number, hi: number) => Math.max(lo + 12, Math.min(hi - 12, v));

  const laidOut: LaidOutNode[] = simNodes.map((n: any) => ({
    id: n.id,
    label: n.label,
    kind: n.kind,
    state: n.state,
    x: clamp(n.x, 0, width),
    y: clamp(n.y, 0, height),
  }));

  const byId = new Map(laidOut.map((n) => [n.id, n] as const));
  const laidEdges: LaidOutEdge[] = edges.map((e) => {
    const s = byId.get(e.source)!;
    const t = byId.get(e.target)!;
    return {
      source: e.source,
      target: e.target,
      sourceX: s.x,
      sourceY: s.y,
      targetX: t.x,
      targetY: t.y,
    };
  });

  return { nodes: laidOut, edges: laidEdges };
}
